import { Box, Button, Divider, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { TextField } from "../TextField/TextField";
import {
  CheckboxVirtualList,
  type CheckboxVirtualListProps,
} from "./CheckboxVirtualList";

type CheckboxesProps<T extends string> = CheckboxVirtualListProps<T> & {
  slotProps?: {
    createButton?: {
      label?: string;
      lengthThreshold?: number;
    };
  };
  isCreateable?: boolean;
  isSearchable?: boolean;
  disableSort?: boolean;
  onCreate?: (value: string) => void;
};

export const Checkboxes = <T extends string>(props: CheckboxesProps<T>) => {
  const {
    isCreateable,
    isSearchable,
    disableSort = false,
    options,
    slotProps,
    value,
    onChange,
    onCreate,
  } = props;
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = useMemo(() => {
    const filtered =
      isSearchable && searchQuery
        ? options.filter((option) =>
            option.label.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : options;

    // Sort alphabetically by label (case-insensitive) unless disabled
    const sorted = disableSort
      ? filtered
      : filtered
          .slice()
          .sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
          );

    // For isSearchable, put checked first, but sort each group alphabetically
    if (isSearchable) {
      const checked = sorted.filter((option) => value.includes(option.value));
      const unchecked = sorted.filter(
        (option) => !value.includes(option.value),
      );
      return [...checked, ...unchecked];
    }

    return sorted;
  }, [options, searchQuery, isSearchable, disableSort, value]);

  return (
    <>
      {isSearchable && (
        <>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <TextField
              placeholder="Search..."
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </Box>
          <Divider />
        </>
      )}
      <CheckboxVirtualList
        options={filteredOptions}
        value={value}
        onChange={onChange}
        height={300}
        estimateSize={38}
      />
      {filteredOptions.length === 0 && (
        <Box sx={{ px: 2 }}>
          <Typography color="text.secondary">No results</Typography>
        </Box>
      )}
      {isCreateable &&
        searchQuery.length >
          (slotProps?.createButton?.lengthThreshold ?? 1) && (
          <Button
            color="secondary"
            size="small"
            onClick={() => onCreate?.(searchQuery)}
            variant="text"
          >
            {`${slotProps?.createButton?.label ?? "+"}: ${searchQuery}`}
          </Button>
        )}
    </>
  );
};
